import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FolderOpen, Search, Eye, Download, Copy, Globe, Lock, FileText, Image as ImageIcon, Upload, RefreshCw, Trash2, ShieldOff } from "lucide-react";

const pickFile = (cb) => { const i = document.createElement("input"); i.type = "file"; i.accept = "image/png,image/jpeg,image/gif,image/webp,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"; i.onchange = () => i.files[0] && cb(i.files[0]); i.click(); };

const BASE = process.env.REACT_APP_BACKEND_URL;
const fmtSize = (n) => (n == null ? "—" : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const isImg = (f) => (f.mime_type || "").startsWith("image/");

export default function FilesAssets() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ q: "", kind: "", visibility: "", page: 1 });
  const load = useCallback(() => {
    api.get("/admin/files", { params: f }).then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e)); setData({ items: [], total: 0, kinds: [] }); });
  }, [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v, page: k === "page" ? v : 1 }));
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="space-y-6" data-testid="files-assets">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><FolderOpen className="w-6 h-6 text-[#006090]" />Files & Assets</h1>
        <p className="text-sm text-slate-500 mt-1">Files and images stored for this account. Every file has a permanent link. Private files (the default) ask recipients to sign in to Top-Recruit with access. Logos, images and documents you've approved can be made public so anyone with the link can open them, for example in emails. Candidate photos and personal records always stay private.</p>
      </div>
      <UploadBar onDone={load} />
      <div className="flex flex-col md:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Search filename, title or type…" className="pl-9 bg-white" data-testid="files-search" />
        </div>
        <select value={f.kind} onChange={(e) => set("kind", e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="files-filter-source">
          <option value="">All sources</option>{(data?.kinds || []).map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
        <select value={f.visibility} onChange={(e) => set("visibility", e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="files-filter-visibility">
          <option value="">Public & private</option><option value="public">Public</option><option value="private">Private</option>
        </select>
      </div>
      {!data ? <p className="text-sm text-slate-400">Loading files…</p> : data.items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid="files-empty">No files match these filters.</div>
      ) : (
        <div className="space-y-2" data-testid="files-list">
          <p className="text-xs text-slate-500" data-testid="files-total">{data.total} file{data.total === 1 ? "" : "s"}</p>
          {data.items.map((it) => <FileRow key={`${it.kind}-${it.id}`} it={it} onChanged={load} />)}
        </div>
      )}
      {pages > 1 && (
        <div className="flex items-center gap-2 justify-end">
          <Button size="sm" variant="outline" disabled={f.page <= 1} onClick={() => set("page", f.page - 1)} data-testid="files-prev">Previous</Button>
          <span className="text-xs text-slate-500">Page {f.page} of {pages}</span>
          <Button size="sm" variant="outline" disabled={f.page >= pages} onClick={() => set("page", f.page + 1)} data-testid="files-next">Next</Button>
        </div>
      )}
    </div>
  );
}

function Thumb({ it }) {
  const [src, setSrc] = useState(it.public_url && isImg(it) ? `${BASE}${it.public_url}` : null);
  useEffect(() => {
    if (it.public_url || !isImg(it)) return undefined;
    let url;
    api.get(`/admin/files/${it.kind}/${it.id}/content`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [it]);
  const Icon = isImg(it) ? ImageIcon : FileText;
  return (
    <div className="w-12 h-12 shrink-0 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden grid place-items-center">
      {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <Icon className="w-5 h-5 text-slate-400" />}
    </div>
  );
}

function UploadBar({ onDone }) {
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const upload = (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("title", title);
    setBusy(true);
    api.post("/admin/files/upload", fd).then(() => { toast.success("Uploaded — it's private until you enable public sharing"); setTitle(""); onDone(); })
      .catch((e) => toast.error(apiError(e))).finally(() => setBusy(false));
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-col sm:flex-row gap-2 sm:items-center" data-testid="files-upload-bar">
      <p className="text-sm font-semibold text-slate-800 shrink-0">Add an email & brand asset</p>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title, e.g. 2026 Service Advantage Program Guide" className="bg-white" data-testid="files-upload-title" />
      <Button size="sm" disabled={busy} onClick={() => pickFile(upload)} className="bg-brand hover:bg-brand-strong shrink-0" data-testid="files-upload-btn"><Upload className="w-3.5 h-3.5 mr-1" />{busy ? "Uploading…" : "Upload file"}</Button>
    </div>
  );
}

function SharingActions({ it, tid, onChanged }) {
  const copy = async () => {
    let data;
    try { ({ data } = await api.post(`/admin/files/${it.kind}/${it.id}/link`)); } catch (e) { toast.error(apiError(e)); return; }
    try { await navigator.clipboard.writeText(data.url); toast.success(data.public ? "Public link copied — opens without sign-in" : "Link copied — recipients must sign in with access"); }
    catch { window.prompt("Copy this permanent link:", data.url); }
    onChanged();
  };
  const setPublic = async (pub) => {
    const msg = pub ? `Make “${it.title || it.filename}” public? Anyone with the link can view it without signing in.`
      : `Revoke public sharing for “${it.title || it.filename}”? The link will require sign-in, and emailed images using it will stop displaying. Attachments already delivered can't be recalled.`;
    if (!window.confirm(msg)) return;
    try { await api.post(`/admin/files/${it.kind}/${it.id}/sharing`, { public: pub }); toast.success(pub ? "Public sharing enabled" : "Public sharing revoked"); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };
  const replace = () => pickFile((file) => {
    if (!window.confirm(`Replace “${it.title || it.filename}” with ${file.name}? Existing links and templates will use the new file. Copies already delivered as attachments won't change.`)) return;
    const fd = new FormData(); fd.append("file", file);
    api.post(`/admin/files/library/${it.id}/replace`, fd).then(() => { toast.success("File replaced — link unchanged"); onChanged(); }).catch((e) => toast.error(apiError(e)));
  });
  const del = async () => {
    if (!window.confirm(`Delete “${it.title || it.filename}”? Templates using it will be flagged and its link will stop working.`)) return;
    try { await api.delete(`/admin/files/library/${it.id}`); toast.success("Deleted"); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };
  return <>
    {!it.public_url && <Button size="sm" variant="outline" onClick={copy} data-testid={`file-copy-link-${tid}`}><Copy className="w-3.5 h-3.5 mr-1" />Copy Link</Button>}
    {it.public_eligible && !it.public && <Button size="sm" variant="outline" onClick={() => setPublic(true)} data-testid={`file-make-public-${tid}`}><Globe className="w-3.5 h-3.5 mr-1" />Make public</Button>}
    {it.public_eligible && it.public && <Button size="sm" variant="outline" onClick={() => setPublic(false)} data-testid={`file-revoke-public-${tid}`}><ShieldOff className="w-3.5 h-3.5 mr-1" />Revoke public</Button>}
    {it.kind === "library" && <Button size="sm" variant="outline" onClick={replace} data-testid={`file-replace-${tid}`}><RefreshCw className="w-3.5 h-3.5 mr-1" />Replace</Button>}
    {it.kind === "library" && <Button size="sm" variant="ghost" onClick={del} className="text-rose-600" data-testid={`file-delete-${tid}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
  </>;
}

const shareLabel = (it) => (it.public_url ? "Public (built-in photo link)" : it.public ? "Public link · no sign-in" : it.public_eligible ? "Private · sign-in link" : "Private · never public");

function FileRow({ it, onChanged }) {
  const [showKey, setShowKey] = useState(false);
  const open = async (download) => {
    if (it.public_url && !download) { window.open(`${BASE}${it.public_url}`, "_blank", "noopener"); return; }
    try {
      const r = await api.get(`/admin/files/${it.kind}/${it.id}/content`, { params: { download }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (download) { const a = document.createElement("a"); a.href = url; a.download = it.filename || "file"; a.click(); }
      else window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiError(e)); }
  };
  const copy = (txt) => navigator.clipboard.writeText(txt).then(() => toast.success("Link copied"));
  const tid = `${it.kind}-${it.id}`;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`file-row-${tid}`}>
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <Thumb it={it} />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-sm text-slate-900 truncate max-w-[22rem]" title={it.title}>{it.title || it.filename}</p>
            <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${it.public ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}`} data-testid={`file-visibility-${tid}`}>
              {it.public ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />}{shareLabel(it)}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5 truncate">{it.filename} · {it.mime_type || "unknown type"} · {fmtSize(it.size)}</p>
          <p className="text-xs text-slate-500 mt-0.5">{it.module} · {it.scope_label} · Uploaded {fmtDate(it.uploaded_at)}{it.uploaded_by_name ? ` by ${it.uploaded_by_name}` : ""}
            {it.linked && <> · Linked: {it.linked.path ? <Link to={it.linked.path} className="text-[#006090] hover:underline">{it.linked.label}</Link> : it.linked.label}</>}</p>
          {showKey && <p className="text-[11px] font-mono text-slate-500 mt-1 break-all" data-testid={`file-storage-key-${tid}`}>{it.storage_key}</p>}
          {it.public_url && <p className="text-[11px] text-slate-500 mt-1 break-all">Public URL: <span className="font-mono">{BASE}{it.public_url}</span></p>}
          {it.share_id && <p className="text-[11px] text-slate-500 mt-1 break-all" data-testid={`file-durable-${tid}`}>Permanent link: <span className="font-mono">{BASE}/api/a/{it.share_id}</span></p>}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 shrink-0">
        <Button size="sm" variant="outline" onClick={() => open(false)} data-testid={`file-view-${tid}`}><Eye className="w-3.5 h-3.5 mr-1" />View</Button>
        <Button size="sm" variant="outline" onClick={() => open(true)} data-testid={`file-download-${tid}`}><Download className="w-3.5 h-3.5 mr-1" />Download</Button>
        {it.public_url && <Button size="sm" variant="outline" onClick={() => copy(`${BASE}${it.public_url}`)} data-testid={`file-copy-url-${tid}`}><Copy className="w-3.5 h-3.5 mr-1" />Copy URL</Button>}
        <SharingActions it={it} tid={tid} onChanged={onChanged} />
        <Button size="sm" variant="ghost" onClick={() => setShowKey((v) => !v)} data-testid={`file-toggle-key-${tid}`} className="text-slate-500">{showKey ? "Hide key" : "Storage key"}</Button>
      </div>
    </div>
  );
}
